import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { deleteWorkflows } from '@/services/workflowDeleteApi';
import { telemetryService } from '@/services/telemetryService';
import { useAppStore } from '@/stores/appStore';
import { useRecentWorkStore } from '@/stores/recentWorkStore';
import { useWorkflowLayoutStore } from '@/stores/workflowLayoutStore';
import {
  composeWorkflowKey,
  useWorkflowViewPrefsStore,
  type WorkflowIdentity,
} from '@/stores/workflowViewPrefsStore';
import { deleteCachedSidecar } from '@/services/nodesMetadataCache';
import { deleteCachedMockData } from '@/services/mockDataCache';

type UseDeleteWorkflowsOptions = {
  onSuccess?: (workflowName: string) => void;
  onError?: (error: Error) => void;
};

export const useDeleteWorkflows = ({ appName, projectName }: { appName: string; projectName: string }, { onSuccess, onError }: UseDeleteWorkflowsOptions = {}) => {
  const { getOttoToken, account } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (workflowName) => {
      return deleteWorkflows({
        projectName,
        appName,
        getToken: getOttoToken,
        workflowNames: [workflowName],
      });
    },
    onSuccess: (_, workflowName) => {
      queryClient.invalidateQueries({
        queryKey: ['workflows', projectName.toLowerCase(), appName.toLowerCase()],
      });
      // Drop every per-workflow React Query cache (designer definition, runs,
      // triggers, repetitions, chat history, agent iterations, etc.) so a
      // future workflow with the same name doesn't inherit stale data.
      queryClient.removeQueries({
        predicate: (query) => query.queryKey.includes(workflowName),
      });
      const tenantId = useAppStore.getState().selectedTenantId;
      // Drop any Recent Activity entry for this workflow so the dashboard
      // doesn't show a dead link to a deleted workflow.
      if (tenantId) {
        useRecentWorkStore.getState().removeByScope({
          tenantId,
          project: projectName,
          app: appName,
          workflow: workflowName,
        });
      }
      // Clean up client-only state keyed by WorkflowIdentity: persisted
      // layout positions, view prefs (orientation), and the in-memory
      // nodesMetadata sidecar cache. We need the same userId+tenantId
      // namespacing that the writers used so we hit the right keys.
      const userId = account?.localAccountId ?? account?.homeAccountId ?? null;
      if (userId && tenantId) {
        const identity: WorkflowIdentity = {
          userId,
          tenantId,
          project: projectName,
          app: appName,
          workflow: workflowName,
        };
        useWorkflowLayoutStore.getState().clearLayout(identity);
        useWorkflowViewPrefsStore.getState().removeByWorkflow(identity);
        deleteCachedSidecar(composeWorkflowKey(identity));
        deleteCachedMockData(composeWorkflowKey(identity));
      }
      telemetryService.trackEvent('workflow.deleted', undefined, { count: 1 });
      onSuccess?.(workflowName);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
};
